import { useFormik } from "formik";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Label, Row } from "reactstrap";
import { Button, InputField } from "src/components";
import { setFromErrors } from "src/helpers";
import { useAcademicStaffWorkloadsEntryMutation } from "src/rtk/features/instituteAndDepartmentRegistration/instituteAndDepartmentRegistrationApi";
import * as Yup from 'yup';

interface WorkLoadOfAcademicStaffProps {
    departmentId?: string;
    academicStaffWorkloadToEdit?: WorkloadStaffEntryList;
    setModalShow?: any;
}

const initialValues: WorkloadStaffEntry = {
    staff_workloads_id: '',
    form_b_department_id: '',
    faculty: '',
    in_patient_care: 0,
    in_patient_rounds: 0,
    out_patient_attendance: 0,
    on_call: 0,
    emergency_attendance: 0,
};

const WorkLoadOfAcademicStaffModal = (props: WorkLoadOfAcademicStaffProps) => {
    const { departmentId, academicStaffWorkloadToEdit, setModalShow } = props;

    const [academicStaffWorkloadRecordEntry, { data: academicStaffWorkloadRecordEntryData, isSuccess: isAcademicStaffWorkloadRecordEntryDataSuccess, isLoading: isAcademicStaffWorkloadRecordEntryDataLoading, isError: isAcademicStaffWorkloadRecordEntryDataError, error: academicStaffWorkloadRecordEntryDataError }] =
        useAcademicStaffWorkloadsEntryMutation();

    const err: CustomError = academicStaffWorkloadRecordEntryDataError as CustomError;

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object({}),

        onSubmit: (values: WorkloadStaffEntry) => {
            console.log(values);
            values.form_b_department_id = departmentId;
            academicStaffWorkloadRecordEntry(values);
        },
    });

    const {
        handleSubmit,
        getFieldProps,
        values,
        handleChange,
        handleBlur,
        touched,
        errors,
        resetForm,
        setValues,
        setErrors,
        setFieldValue,
    } = formik;

    const handleFormReset = () => {
        resetForm();
    };

    useEffect(() => {
        if (academicStaffWorkloadToEdit?.id) {
            setValues({
                staff_workloads_id: academicStaffWorkloadToEdit?.id,
                form_b_department_id: academicStaffWorkloadToEdit?.form_b_department_id,
                faculty: academicStaffWorkloadToEdit?.faculty,
                in_patient_care: academicStaffWorkloadToEdit?.in_patient_care,
                in_patient_rounds: academicStaffWorkloadToEdit?.in_patient_rounds,
                out_patient_attendance: academicStaffWorkloadToEdit?.out_patient_attendance,
                on_call: academicStaffWorkloadToEdit?.on_call,
                emergency_attendance: academicStaffWorkloadToEdit?.emergency_attendance,
            });
        }
    }, []);

    useEffect(() => {
        if (isAcademicStaffWorkloadRecordEntryDataSuccess) {
            handleFormReset();
            toast.success('Work load academic staff record saved successfully');
            setModalShow(false);
        }
        if (isAcademicStaffWorkloadRecordEntryDataError) {
            Object.entries(err?.data).forEach((entry: any) => {
                entry[1]?.forEach((message: any) => {
                    toast.error(message);
                });
            });

            setFromErrors(err.data, setErrors);
        }
    }, [isAcademicStaffWorkloadRecordEntryDataSuccess, isAcademicStaffWorkloadRecordEntryDataError]);

    return (
        <React.Fragment>
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader>
                                <h5 className="card-title">
                                    <i className="fa fa-plus me-2" />
                                    Work load of academic staff
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <Form
                                    autoComplete="off"
                                    className="custom-form"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSubmit();
                                    }}
                                    onReset={(e) => {
                                        e.preventDefault();
                                        handleFormReset();
                                    }}
                                >
                                    <Row>
                                        <Col sm={12} md={6}>
                                            <FormGroup className="mb-3">
                                                <Label for="faculty">Faculty</Label>
                                                <InputField
                                                    type="select"
                                                    options={[
                                                        {label: 'Professor', value: 'Professor'},
                                                        {label: 'Associate Professor', value: 'Associate Professor'},
                                                        {label: 'Assistant Professor', value: 'Assistant Professor'},
                                                        {label: 'Consultant', value: 'Consultant'},                                                
                                                    ]}
                                                    placeholder=""
                                                    {...getFieldProps('faculty')}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup className="mb-3">
                                                <Label for="in_patient_care">In-Patient Care</Label>
                                                <InputField
                                                    type="number"
                                                    placeholder=""
                                                    {...getFieldProps('in_patient_care')}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup className="mb-3">
                                                <Label for="in_patient_rounds">In-Patient Rounds</Label>
                                                <InputField
                                                    type="number"
                                                    placeholder=""
                                                    {...getFieldProps('in_patient_rounds')}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup className="mb-3">
                                                <Label for="out_patient_attendance">Out-Patient Attendance</Label>
                                                <InputField
                                                    type="number"
                                                    placeholder=""
                                                    {...getFieldProps('out_patient_attendance')}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup row className="mb-3">
                                                <Label className="font-size-14 mb-3" sm={6}>On Call:</Label>
                                                <Col sm={3}>
                                                    <div className="d-inline-block mx-2 mt-2">
                                                        <div className="form-check form-check-left mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="onCall"
                                                                id="onCallYes"
                                                                value={1}
                                                                checked={values.on_call === 1}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setFieldValue('on_call', 1);
                                                                    }
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor="onCallYes">Yes</label>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col sm={3}>
                                                    <div className="d-inline-block mx-2 mt-2">
                                                        <div className="form-check form-check-left">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="onCall"
                                                                id="onCallNo"
                                                                value={0}
                                                                checked={values.on_call === 0}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setFieldValue('on_call', 0);
                                                                    }
                                                                }}
                                                                defaultChecked
                                                            />
                                                            <label className="form-check-label" htmlFor="onCallNo">No</label>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} md={6}>
                                            <FormGroup row className="mb-3">
                                                <Label className="font-size-14 mb-3" sm={6}>Emergency Attendance:</Label>
                                                <Col sm={3}>
                                                    <div className="d-inline-block mx-2 mt-2">
                                                        <div className="form-check form-check-left mb-3">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="emergencyAttendance"
                                                                id="emergencyAttendanceYes"
                                                                value={1}
                                                                checked={values.emergency_attendance === 1}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setFieldValue('emergency_attendance', 1);
                                                                    }
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor="emergencyAttendanceYes">Yes</label>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col sm={3}>
                                                    <div className="d-inline-block mx-2 mt-2">
                                                        <div className="form-check form-check-left">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="emergencyAttendance"
                                                                id="emergencyAttendanceNo"
                                                                value={0}
                                                                checked={values.emergency_attendance === 0}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setFieldValue('emergency_attendance', 0);
                                                                    }
                                                                }}
                                                                defaultChecked
                                                            />
                                                            <label className="form-check-label" htmlFor="emergencyAttendanceNo">No</label>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </FormGroup>
                                        </Col>

                                        <Col sm={12} className="mt-3">
                                            <FormGroup className="mb-3 text-center">
                                                <Button
                                                    type="reset"
                                                    text="Reset"
                                                    className="btn btn-warning me-2"
                                                ></Button>
                                                <Button
                                                    type="submit"
                                                    text="Save"
                                                    className="btn btn-success me-2"
                                                    disabled={isAcademicStaffWorkloadRecordEntryDataLoading}
                                                ></Button>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </React.Fragment>
    );
};

export default WorkLoadOfAcademicStaffModal;